import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

const RECEIPT_MATCH_BADGES = {
  'same-month': { label: 'Will Update', bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
  'other-month': { label: 'New Month Row', bg: '#dbeafe', color: '#1e40af', dot: '#2563eb' },
  'no-match': { label: 'New Student', bg: '#f3e8ff', color: '#6b21a8', dot: '#9333ea' },
};

const RECEIPT_IMPORT_COLUMN_ALIASES = {
  receiptId: ['receipt id', 'receipt no', 'receipt number'],
  transactionDate: ['transaction date', 'paid date', 'date'],
  regId: ['registration id', 'registration no', 'reg id', 'reg no'],
  course: ['course'],
  student: ['student', 'student name'],
  paymentType: ['payment type', 'payment method', 'payment'],
  amount: ['amount', 'paid amount'],
};

const normalizeReceiptImportHeader = (value) => String(value || '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/[().:_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const getReceiptImportField = (header) => {
  const normalized = normalizeReceiptImportHeader(header);
  return Object.entries(RECEIPT_IMPORT_COLUMN_ALIASES).find(([, aliases]) => aliases.includes(normalized))?.[0] || null;
};

const cleanReceiptImportCell = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const fmt = (value) => {
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  if (Number.isNaN(parsed)) return '0';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(parsed);
};

const formatMonthTitle = (value) => {
  if (!value) return 'All Months';
  const parsed = new Date(`${value}-01`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('default', { month: 'long', year: 'numeric' });
};

const toMonthKey = (dateValue) => {
  const value = String(dateValue || '').trim();
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (match) return `${match[1]}-${match[2]}`;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

const parseReceiptImportTable = (input) => {
  const source = String(input || '').trim();

  if (!source) {
    return { rows: [], error: 'Paste the receipt table HTML first.' };
  }

  let headers = [];
  let rawRows = [];

  if (/<table[\s>]|<tr[\s>]|<td[\s>]|<th[\s>]/i.test(source)) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(source, 'text/html');
    const table = doc.querySelector('table');

    if (!table) {
      return { rows: [], error: 'No HTML table was found in the pasted content.' };
    }

    const headerCells = Array.from(table.querySelectorAll('thead tr:first-child th, thead tr:first-child td'));
    headers = headerCells.map((cell) => cleanReceiptImportCell(cell.textContent));

    const rowElements = table.querySelectorAll('tbody tr').length
      ? Array.from(table.querySelectorAll('tbody tr'))
      : Array.from(table.querySelectorAll('tr')).slice(1);

    rawRows = rowElements.map((row) => Array.from(row.querySelectorAll('td, th')).map((cell) => cleanReceiptImportCell(cell.textContent)));
  } else {
    const lines = source.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length < 2) {
      return { rows: [], error: 'Paste an HTML table or tab-separated rows with a header line.' };
    }

    headers = lines[0].split('\t').map((cell) => cleanReceiptImportCell(cell));
    rawRows = lines.slice(1).map((line) => line.split('\t').map((cell) => cleanReceiptImportCell(cell)));
  }

  const fieldIndexes = headers.map((header) => getReceiptImportField(header));

  if (!fieldIndexes.some(Boolean)) {
    return { rows: [], error: 'The pasted table does not contain the required receipt columns.' };
  }

  const rows = rawRows
    .map((cells) => {
      const mapped = {
        receiptId: '',
        transactionDate: '',
        regId: '',
        course: '',
        student: '',
        paymentType: '',
        amount: '',
      };

      fieldIndexes.forEach((field, index) => {
        if (field) {
          mapped[field] = cleanReceiptImportCell(cells[index]);
        }
      });

      return mapped;
    })
    .filter((row) => Object.values(row).some(Boolean))
    .map((row, index) => {
      const errors = [];
      const normalizedAmount = row.amount ? row.amount.replace(/,/g, '').trim() : '';

      if (!row.regId) errors.push('Registration ID is required.');
      if (!row.transactionDate) errors.push('Transaction Date is required.');
      if (!normalizedAmount || Number.isNaN(Number(normalizedAmount))) errors.push('Amount must be a valid number.');

      return {
        rowNumber: index + 1,
        ...row,
        amount: normalizedAmount,
        errors,
      };
    });

  if (rows.length === 0) {
    return { rows: [], error: 'No receipt rows were found in the pasted content.' };
  }

  return { rows, error: '' };
};

function buildRecordIndex(records) {
  const map = new Map();
  (Array.isArray(records) ? records : []).forEach((record) => {
    const regId = String(record?.studentId || '').trim().toLowerCase();
    if (!regId) return;
    if (!map.has(regId)) map.set(regId, []);
    map.get(regId).push(record);
  });
  return map;
}

export default function PaidReceiptsImportModal({
  isOpen,
  onClose,
  records = [],
  monthFilter = '',
  onImported,
}) {
  const [input, setInput] = useState('');
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inputCollapsed, setInputCollapsed] = useState(false);

  const recordIndex = useMemo(() => buildRecordIndex(records), [records]);

  const validRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const blockedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);

  const sameMonthRows = useMemo(() => validRows.filter((row) => row.matchStatus === 'same-month'), [validRows]);
  const otherMonthRows = useMemo(() => validRows.filter((row) => row.matchStatus === 'other-month'), [validRows]);
  const noMatchRows = useMemo(() => validRows.filter((row) => row.matchStatus === 'no-match'), [validRows]);

  const totalAmount = useMemo(() => (
    validRows.reduce((sum, row) => {
      const val = parseFloat(row.amount);
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0)
  ), [validRows]);

  useEffect(() => {
    if (!isOpen) {
      setInput('');
      setRows([]);
      setError('');
      setSubmitting(false);
      setInputCollapsed(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const previewReceiptImport = () => {
    const parsed = parseReceiptImportTable(input);
    if (parsed.rows.length === 0) {
      setRows([]);
      setError(parsed.error);
      setInputCollapsed(false);
      return;
    }

    const enrichedRows = parsed.rows.map((row) => {
      const regId = String(row.regId || '').trim().toLowerCase();
      const relatedRecords = regId ? (recordIndex.get(regId) || []) : [];
      const txMonth = toMonthKey(row.transactionDate);

      let matchStatus = 'no-match';
      let matchedRecord = null;

      if (relatedRecords.length > 0) {
        const sameMonthRecord = txMonth
          ? relatedRecords.find((record) => String(record?.reportMonth || '').slice(0, 7) === txMonth)
          : null;

        if (sameMonthRecord) {
          matchStatus = 'same-month';
          matchedRecord = sameMonthRecord;
        } else {
          matchStatus = 'other-month';
          matchedRecord = [...relatedRecords].sort((a, b) => (
            String(b?.reportMonth || '').localeCompare(String(a?.reportMonth || ''))
          ))[0] || null;
        }
      }

      return { ...row, matchStatus, matchedRecord };
    });

    setRows(enrichedRows);
    setError(parsed.error || '');
    setInputCollapsed(enrichedRows.length > 0);
  };

  const submitReceiptImport = async () => {
    if (validRows.length === 0) {
      setError(rows.length > 0
        ? 'Fix the blocked rows before importing.'
        : 'Preview the pasted table before importing.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const payloadRows = validRows.map((row) => ({
        receiptId: row.receiptId,
        transactionDate: row.transactionDate,
        regId: row.regId,
        course: row.course,
        student: row.student,
        paymentType: row.paymentType,
        amount: row.amount,
      }));

      const response = await axiosInstance.post('/admin/recovery-dues/import-paid-receipts', {
        rows: payloadRows,
      });

      const data = response?.data || {};
      if (onImported) {
        await onImported({
          updated: data.updated || 0,
          inserted: data.inserted || 0,
          skipped: data.skipped || 0,
          errors: data.errors || 0,
          hasIssues: (data.errors || 0) > 0 || (data.skipped || 0) > 0,
        });
      }
      if (onClose) onClose();
    } catch (submitError) {
      setError(submitError?.response?.data?.message || 'Failed to import paid receipts.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.62)', backdropFilter: 'blur(2px)' }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="paid-receipt-import-title"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 1200,
          maxHeight: '95vh',
          overflow: 'hidden',
          background: '#f8fafc',
          borderRadius: 20,
          boxShadow: '0 32px 80px rgba(15, 23, 42, 0.32)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid #e2e8f0',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          borderRadius: '20px 20px 0 0',
        }}>
          <div>
            <h3 id="paid-receipt-import-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Import Paid Receipts</h3>
            <div style={{ marginTop: 3, fontSize: 13, color: '#64748b' }}>
              Paste a receipt table, preview matches, then confirm to mark dues as paid.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {rows.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {Object.entries(RECEIPT_MATCH_BADGES).map(([key, badge]) => (
                  <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: badge.bg, color: badge.color, borderRadius: 999, padding: '4px 11px', fontSize: 11, fontWeight: 700 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: badge.dot, display: 'inline-block' }} />
                    {badge.label}
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{ width: 36, height: 36, borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
            >
              x
            </button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '18px 24px' }}>
            {inputCollapsed ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Receipt HTML pasted</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {input.length.toLocaleString()} characters and {rows.length} data row{rows.length !== 1 ? 's' : ''} parsed
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setInputCollapsed(false)}
                    style={{ height: 36, padding: '0 14px', borderRadius: 9, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Edit Paste
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInput('');
                      setRows([]);
                      setError('');
                      setInputCollapsed(false);
                    }}
                    style={{ height: 36, padding: '0 14px', borderRadius: 9, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <label htmlFor="paid-receipt-import-input" style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Paste Receipt Table HTML
                  </label>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Columns: Receipt ID, Transaction Date, Registration ID, Course, Student, Payment Type, Amount</span>
                </div>
                <textarea
                  id="paid-receipt-import-input"
                  autoFocus
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setRows([]);
                    setError('');
                    setInputCollapsed(false);
                  }}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                      e.preventDefault();
                      previewReceiptImport();
                    }
                  }}
                  rows={7}
                  placeholder="Paste the full HTML table here... (Ctrl+Enter to preview)"
                  style={{
                    width: '100%',
                    borderRadius: 12,
                    border: '1px solid #cbd5e1',
                    padding: '12px 14px',
                    fontSize: 12,
                    color: '#0f172a',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'Consolas, ui-monospace, monospace',
                    lineHeight: 1.5,
                    background: '#f8fafc',
                  }}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={previewReceiptImport}
                    disabled={!input.trim()}
                    style={{
                      height: 40,
                      padding: '0 20px',
                      borderRadius: 10,
                      border: 'none',
                      background: input.trim() ? '#2563eb' : '#cbd5e1',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: input.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Preview Table
                  </button>
                  {input && (
                    <button
                      type="button"
                      onClick={() => {
                        setInput('');
                        setRows([]);
                        setError('');
                        setInputCollapsed(false);
                      }}
                      style={{ height: 40, padding: '0 16px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  )}
                  <span style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center' }}>Ctrl+Enter to preview</span>
                </div>
              </>
            )}

            {error && (
              <div style={{ marginTop: 12, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>
                {error}
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              padding: '16px 24px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}>
              {[
                { label: 'Total Rows', value: rows.length, color: '#0f172a', bg: '#fff', border: '#e2e8f0' },
                { label: 'Will Update', value: sameMonthRows.length, color: '#166534', bg: '#f0fdf4', border: '#bbf7d0', sub: 'same month matched' },
                { label: 'New Month Row', value: otherMonthRows.length, color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe', sub: 'reg found, diff month' },
                { label: 'New Student', value: noMatchRows.length, color: '#6b21a8', bg: '#faf5ff', border: '#ddd6fe', sub: 'not in loaded dues' },
                { label: 'Blocked', value: blockedRows.length, color: '#be123c', bg: '#fff1f2', border: '#fecaca', sub: 'validation errors' },
                { label: 'Total Amount', value: `Rs ${fmt(totalAmount)}`, color: '#0f172a', bg: '#fff', border: '#e2e8f0', sub: 'import total', isText: true },
              ].map((item) => (
                <div key={item.label} style={{ background: item.bg, borderRadius: 12, border: `1px solid ${item.border}`, padding: '13px 16px' }}>
                  <div style={{ fontSize: item.isText ? 16 : 24, fontWeight: 700, color: item.color, lineHeight: 1.1 }}>{item.value}</div>
                  <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: item.color }}>{item.label}</div>
                  {item.sub && <div style={{ marginTop: 2, fontSize: 11, color: '#94a3b8' }}>{item.sub}</div>}
                </div>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <div style={{ padding: '0 24px 24px' }}>
              <div style={{ marginTop: 16, marginBottom: 10, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Preview | {rows.length} Receipt{rows.length !== 1 ? 's' : ''}
                {monthFilter && (
                  <span style={{ marginLeft: 8, fontWeight: 500, textTransform: 'none', color: '#94a3b8' }}>
                    - cross-referenced against {formatMonthTitle(monthFilter)}
                  </span>
                )}
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Row</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Receipt</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Student</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Transaction</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Amount</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Match</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#475569' }}>Validation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const badge = RECEIPT_MATCH_BADGES[row.matchStatus] || { label: 'Unknown', bg: '#f1f5f9', color: '#64748b', dot: '#94a3b8' };
                        const hasErrors = row.errors.length > 0;
                        return (
                          <tr key={`${row.rowNumber}-${row.receiptId || row.regId || 'row'}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>#{row.rowNumber}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#0f172a', fontWeight: 600 }}>{row.receiptId || 'andmdash;'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: '#334155' }}>{row.regId || 'andmdash;'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: '#334155' }}>{row.transactionDate || 'andmdash;'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: '#0f172a', fontWeight: 700 }}>Rs {fmt(row.amount)}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: badge.bg, color: badge.color, borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: badge.dot, display: 'inline-block' }} />
                                {badge.label}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: hasErrors ? '#b91c1c' : '#166534' }}>
                              {hasErrors ? row.errors.join(' ') : 'Ready'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{
          padding: '14px 20px',
          borderTop: '1px solid #e2e8f0',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 13, color: '#475569' }}>
            {rows.length > 0 ? (
              <>
                Ready: <strong style={{ color: '#166534' }}>{validRows.length}</strong>{' '}
                Blocked: <strong style={{ color: '#be123c' }}>{blockedRows.length}</strong>
              </>
            ) : (
              'Paste a receipt table and preview before importing.'
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                height: 40,
                padding: '0 14px',
                borderRadius: 10,
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: '#475569',
                fontSize: 13,
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={submitReceiptImport}
              disabled={submitting || validRows.length === 0}
              style={{
                height: 40,
                padding: '0 16px',
                borderRadius: 10,
                border: 'none',
                background: submitting || validRows.length === 0 ? '#86efac' : '#16a34a',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: submitting || validRows.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Importing...' : `Confirm and Import ${validRows.length > 0 ? validRows.length : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
